import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, CheckCircle2, XCircle, RefreshCw, RotateCcw, Sparkles, ShieldQuestion, Gavel } from "lucide-react";
import { toast } from "sonner";
import { bumpDocVersion } from "@/lib/docVersion";

type Proposal = {
  id: string;
  doc_id: string;
  base_version: string;
  proposed_body: string;
  editor_id: string;
  editor_note: string | null;
  status: string;
  substance_check: any;
  auto_approved: boolean | null;
  created_at: string;
  updated_at: string;
  escalation_justification: string | null;
  escalation_finding_index: number | null;
  escalation_finding_detail: string | null;
  escalated_at: string | null;
  escalation_resolution: string | null;
  escalation_resolved_at: string | null;
};

type FilterKey =
  | "manual_queue"
  | "escalated"
  | "auto_approved"
  | "approved_all"
  | "needs_revision"
  | "superseded"
  | "all";

const LAST_VIEWED_KEY = "reviewer_edits_admin.last_viewed_at";

function inlineDiff(base: string, proposed: string) {
  const bl = base.split("\n");
  const pl = proposed.split("\n");
  const max = Math.max(bl.length, pl.length);
  const rows: { base: string; proposed: string; changed: boolean }[] = [];
  for (let i = 0; i < max; i++) {
    const b = bl[i] ?? "";
    const p = pl[i] ?? "";
    rows.push({ base: b, proposed: p, changed: b !== p });
  }
  return rows;
}

export function ReviewerEditsAdmin() {
  const [rows, setRows] = useState<Proposal[]>([]);
  const [docs, setDocs] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [acting, setActing] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterKey>("manual_queue");
  const [lastViewedAt, setLastViewedAt] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    const { data, error } = await supabase
      .from("doc_edit_proposals" as any)
      .select("*")
      .in("status", ["checked", "needs_revision", "superseded", "approved", "rejected", "escalated"])
      .order("updated_at", { ascending: false });
    if (error) {
      toast.error(error.message);
      setLoading(false);
      return;
    }
    const list = ((data as any[]) || []) as Proposal[];
    setRows(list);

    const ids = Array.from(new Set(list.map((r) => r.doc_id)));
    if (ids.length) {
      const { data: docsData } = await supabase
        .from("sop_documents" as any)
        .select("doc_id,title,version,body_markdown,requires_founder_review,collection,domain")
        .in("doc_id", ids);
      const map: Record<string, any> = {};
      for (const d of (docsData as any[]) || []) map[d.doc_id] = d;
      setDocs(map);
    }
    setLoading(false);
  }

  useEffect(() => {
    setLastViewedAt(localStorage.getItem(LAST_VIEWED_KEY));
    load();
  }, []);

  // Mark viewed when the operator lands on the auto-approved tab so the count
  // badge resets after they actually look at the fresh audit rows.
  useEffect(() => {
    if (filter === "auto_approved") {
      const now = new Date().toISOString();
      localStorage.setItem(LAST_VIEWED_KEY, now);
      setLastViewedAt(now);
    }
  }, [filter]);

  const counts = useMemo(() => {
    const c = {
      manual_queue: 0,
      escalated: 0,
      auto_approved: 0,
      approved_all: 0,
      needs_revision: 0,
      superseded: 0,
      escalations_approved: 0,
      escalations_upheld: 0,
    };
    for (const r of rows) {
      if (r.status === "checked") c.manual_queue++;
      else if (r.status === "escalated") c.escalated++;
      else if (r.status === "approved") {
        c.approved_all++;
        if (r.auto_approved) c.auto_approved++;
      } else if (r.status === "needs_revision") c.needs_revision++;
      else if (r.status === "superseded") c.superseded++;
      if (r.escalation_resolution === "approved") c.escalations_approved++;
      if (r.escalation_resolution === "upheld") c.escalations_upheld++;
    }
    return c;
  }, [rows]);

  const newAutoApprovals = useMemo(() => {
    if (!lastViewedAt) return counts.auto_approved;
    return rows.filter(
      (r) => r.status === "approved" && r.auto_approved && r.updated_at > lastViewedAt,
    ).length;
  }, [rows, lastViewedAt, counts.auto_approved]);

  const filtered = useMemo(() => {
    switch (filter) {
      case "manual_queue":
        return rows.filter((r) => r.status === "checked");
      case "escalated":
        return rows.filter((r) => r.status === "escalated");
      case "auto_approved":
        return rows.filter((r) => r.status === "approved" && r.auto_approved);
      case "approved_all":
        return rows.filter((r) => r.status === "approved");
      case "needs_revision":
        return rows.filter((r) => r.status === "needs_revision");
      case "superseded":
        return rows.filter((r) => r.status === "superseded");
      default:
        return rows;
    }
  }, [rows, filter]);

  async function approve(p: Proposal) {
    const d = docs[p.doc_id];
    if (!d) {
      toast.error("Document not found.");
      return;
    }
    if (d.version !== p.base_version) {
      toast.error("This proposal is superseded by a newer version.");
      return;
    }
    if (!confirm(`Approve and publish this edit to ${p.doc_id}?`)) return;

    setActing(p.id);
    try {
      const newVersion = bumpDocVersion(d.version);
      const { error: upErr } = await supabase
        .from("sop_documents" as any)
        .update({
          body_markdown: p.proposed_body,
          version: newVersion,
          change_note: "clarity edit by reviewer, substance-checked",
        })
        .eq("doc_id", p.doc_id);
      if (upErr) throw upErr;

      const wasEscalated = p.status === "escalated";
      const proposalUpdate: Record<string, unknown> = {
        status: "approved",
        auto_approved: false,
      };
      if (wasEscalated) {
        proposalUpdate.escalation_resolution = "approved";
        proposalUpdate.escalation_resolved_at = new Date().toISOString();
      }
      const { error: pErr } = await supabase
        .from("doc_edit_proposals" as any)
        .update(proposalUpdate)
        .eq("id", p.id);
      if (pErr) throw pErr;

      supabase.functions.invoke("embed-docs", { body: { doc_ids: [p.doc_id] } }).catch(() => {});
      supabase.functions.invoke("link-audit").catch(() => {});

      toast.success(`Published as v${newVersion}. Search index refresh triggered.`);
      await load();
    } catch (e: any) {
      toast.error(e?.message || "Approve failed.");
    } finally {
      setActing(null);
    }
  }

  async function reject(p: Proposal) {
    if (!confirm("Reject this proposal? The reviewer will see it as rejected.")) return;
    setActing(p.id);
    try {
      const { error } = await supabase
        .from("doc_edit_proposals" as any)
        .update({ status: "rejected" })
        .eq("id", p.id);
      if (error) throw error;
      toast.success("Rejected.");
      await load();
    } catch (e: any) {
      toast.error(e?.message || "Reject failed.");
    } finally {
      setActing(null);
    }
  }

  async function uphold(p: Proposal) {
    if (!confirm("Uphold the substance-check finding? The reviewer's proposal will be rejected.")) return;
    setActing(p.id);
    try {
      const { error } = await supabase
        .from("doc_edit_proposals" as any)
        .update({
          status: "rejected",
          escalation_resolution: "upheld",
          escalation_resolved_at: new Date().toISOString(),
        })
        .eq("id", p.id);
      if (error) throw error;
      toast.success("Escalation upheld. Proposal rejected.");
      await load();
    } catch (e: any) {
      toast.error(e?.message || "Uphold failed.");
    } finally {
      setActing(null);
    }
  }

  async function revert(p: Proposal) {
    const d = docs[p.doc_id];
    if (!d) {
      toast.error("Document not found.");
      return;
    }
    if (
      !confirm(
        `Revert ${p.doc_id} to the prior body?\n\nThis restores the most recent snapshot before the current version and bumps the version again.`,
      )
    ) {
      return;
    }
    setActing(p.id);
    try {
      // Most recent snapshot in sop_revisions is the state that existed just
      // before the current version was written.
      const { data: rev, error: rErr } = await supabase
        .from("sop_revisions" as any)
        .select("version, body_markdown")
        .eq("doc_id", p.doc_id)
        .order("archived_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (rErr) throw rErr;
      if (!rev) {
        toast.error("No prior revision exists to revert to.");
        setActing(null);
        return;
      }
      const prior = rev as any;
      const newVersion = bumpDocVersion(d.version);
      const { error: upErr } = await supabase
        .from("sop_documents" as any)
        .update({
          body_markdown: prior.body_markdown,
          version: newVersion,
          change_note: `reverted from v${d.version} to prior v${prior.version} content`,
        })
        .eq("doc_id", p.doc_id);
      if (upErr) throw upErr;

      supabase.functions.invoke("embed-docs", { body: { doc_ids: [p.doc_id] } }).catch(() => {});
      supabase.functions.invoke("link-audit").catch(() => {});
      toast.success(`Reverted. Published as v${newVersion}.`);
      await load();
    } catch (e: any) {
      toast.error(e?.message || "Revert failed.");
    } finally {
      setActing(null);
    }
  }

  const filterButtons: { key: FilterKey; label: string; badgeCount?: number; highlight?: boolean }[] = [
    { key: "manual_queue", label: "Manual queue", badgeCount: counts.manual_queue },
    {
      key: "escalated",
      label: "Escalated",
      badgeCount: counts.escalated,
      highlight: counts.escalated > 0,
    },
    {
      key: "auto_approved",
      label: "Auto-approved",
      badgeCount: newAutoApprovals > 0 ? newAutoApprovals : counts.auto_approved,
      highlight: newAutoApprovals > 0,
    },
    { key: "approved_all", label: "All approved", badgeCount: counts.approved_all },
    { key: "needs_revision", label: "Needs revision", badgeCount: counts.needs_revision },
    { key: "superseded", label: "Superseded", badgeCount: counts.superseded },
    { key: "all", label: "All" },
  ];

  const totalResolved = counts.escalations_approved + counts.escalations_upheld;
  const approvedPct = totalResolved > 0
    ? Math.round((counts.escalations_approved / totalResolved) * 100)
    : null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          {filterButtons.map((b) => (
            <Button
              key={b.key}
              variant={filter === b.key ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter(b.key)}
              className="relative"
            >
              {b.label}
              {typeof b.badgeCount === "number" && b.badgeCount > 0 && (
                <Badge
                  variant={b.highlight ? "default" : "secondary"}
                  className={`ml-2 ${b.highlight ? "bg-gold text-charcoal" : ""}`}
                >
                  {b.badgeCount}
                  {b.highlight ? " new" : ""}
                </Badge>
              )}
            </Button>
          ))}
        </div>
        <Button variant="ghost" size="sm" onClick={load}>
          <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
        </Button>
      </div>

      {(totalResolved > 0 || counts.escalated > 0) && (
        <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
          <ShieldQuestion className="h-3.5 w-3.5" />
          Escalations: {counts.escalations_approved} approved · {counts.escalations_upheld} upheld
          {approvedPct !== null && <span>({approvedPct}% approved)</span>}
          {counts.escalated > 0 && <span>· {counts.escalated} awaiting review</span>}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <Card className="p-8 text-center text-muted-foreground text-sm">
          Nothing in this view.
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((p) => {
            const d = docs[p.doc_id];
            const isOpen = expanded === p.id;
            const diff = d && isOpen ? inlineDiff(d.body_markdown || "", p.proposed_body) : [];
            const isApproved = p.status === "approved";
            const isAuto = isApproved && !!p.auto_approved;
            const isEscalated = p.status === "escalated";
            const flagged = !!d?.requires_founder_review;
            return (
              <Card key={p.id} className="p-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{d?.title || p.doc_id}</span>
                      {flagged && (
                        <span
                          className="inline-block h-2 w-2 rounded-full bg-gold"
                          title="Founder review flag on this doc: spot-check the clarity edit"
                          aria-label="Founder review flag"
                        />
                      )}
                      <Badge variant="outline" className="text-xs font-mono">
                        {p.doc_id}
                      </Badge>
                      <Badge variant="secondary" className="text-xs">
                        base v{p.base_version}
                      </Badge>

                      {isAuto && (
                        <Badge variant="secondary" className="text-xs flex items-center gap-1">
                          <Sparkles className="h-3 w-3" /> Auto-approved
                        </Badge>
                      )}
                      {isEscalated && (
                        <Badge className="text-xs flex items-center gap-1 bg-amber-500 hover:bg-amber-500 text-white">
                          <ShieldQuestion className="h-3 w-3" /> Escalated
                        </Badge>
                      )}
                      <Badge
                        variant={isApproved ? "default" : "outline"}
                        className="text-xs"
                      >
                        {p.status}
                      </Badge>
                      {p.escalation_resolution && (
                        <Badge variant="outline" className="text-xs">
                          escalation {p.escalation_resolution}
                        </Badge>
                      )}
                    </div>
                    {p.editor_note && (
                      <div className="text-xs text-muted-foreground mt-1">
                        Note: {p.editor_note}
                      </div>
                    )}
                    {isEscalated && p.escalation_justification && (
                      <div className="text-xs mt-2 bg-amber-50 border border-amber-200 rounded p-2 max-w-2xl">
                        <div className="font-semibold text-amber-900 flex items-center gap-1">
                          <ShieldQuestion className="h-3 w-3" /> Reviewer justification
                        </div>
                        <div className="text-amber-900 mt-1">{p.escalation_justification}</div>
                        {p.escalation_finding_detail && (
                          <div className="text-[11px] text-muted-foreground mt-1">
                            Contested finding: {p.escalation_finding_detail}
                          </div>
                        )}
                      </div>
                    )}
                    <div className="text-[11px] text-muted-foreground mt-1">
                      Updated {new Date(p.updated_at).toLocaleString()}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setExpanded(isOpen ? null : p.id)}
                    >
                      {isOpen ? "Hide diff" : "Show diff"}
                    </Button>
                    {p.status === "checked" && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => reject(p)}
                          disabled={acting === p.id}
                        >
                          <XCircle className="h-3.5 w-3.5 mr-1" /> Reject
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => approve(p)}
                          disabled={acting === p.id}
                        >
                          {acting === p.id ? (
                            <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                          ) : (
                            <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                          )}
                          Approve & publish
                        </Button>
                      </>
                    )}
                    {isEscalated && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => uphold(p)}
                          disabled={acting === p.id}
                          title="Reject the proposal and record that the substance-check finding stands"
                        >
                          <Gavel className="h-3.5 w-3.5 mr-1" /> Uphold finding
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => approve(p)}
                          disabled={acting === p.id}
                          title="Publish the reviewer's version as written"
                        >
                          {acting === p.id ? (
                            <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                          ) : (
                            <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                          )}
                          Publish as written
                        </Button>
                      </>
                    )}
                    {isApproved && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => revert(p)}
                        disabled={acting === p.id}
                        title="Restore the prior body and bump the version"
                      >
                        {acting === p.id ? (
                          <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                        ) : (
                          <RotateCcw className="h-3.5 w-3.5 mr-1" />
                        )}
                        Revert
                      </Button>
                    )}
                  </div>
                </div>

                {isOpen && (
                  <div className="mt-4 space-y-3">
                    {p.substance_check && (
                      <div className="text-xs bg-muted/40 rounded p-2">
                        <div className="font-medium mb-1">Checks</div>
                        <div>
                          Passed:{" "}
                          <span className={p.substance_check.passed ? "text-green-700" : "text-red-700"}>
                            {String(!!p.substance_check.passed)}
                          </span>
                          {p.substance_check.auto_approved && (
                            <span className="ml-2 text-muted-foreground">
                              (auto-published as v{p.substance_check.published_version})
                            </span>
                          )}
                        </div>
                        {p.substance_check.publish_error && (
                          <div className="mt-1 text-red-700">
                            Publish blocked: {p.substance_check.publish_error}
                          </div>
                        )}
                        {(p.substance_check.mechanical || []).length > 0 && (
                          <ul className="mt-1 list-disc pl-4">
                            {(p.substance_check.mechanical || []).map((f: any, i: number) => (
                              <li key={i}>
                                <span className="font-medium">{f.category}:</span> {f.detail}
                              </li>
                            ))}
                          </ul>
                        )}
                        {p.substance_check.ai?.findings?.length > 0 && (
                          <ul className="mt-1 list-disc pl-4">
                            {p.substance_check.ai.findings.map((f: any, i: number) => (
                              <li key={i}>
                                <span className="font-medium">Substance:</span> {f.detail}
                              </li>
                            ))}
                          </ul>
                        )}
                        {p.substance_check.proofread?.findings?.length > 0 && (
                          <ul className="mt-1 list-disc pl-4">
                            {p.substance_check.proofread.findings.map((f: any, i: number) => (
                              <li key={i}>
                                <span className="font-medium">Proofread:</span> {f.detail}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                    {d ? (
                      <div className="grid grid-cols-2 gap-2 text-xs font-mono">
                        <div className="text-muted-foreground uppercase tracking-wide">
                          Current v{d.version}
                        </div>
                        <div className="text-muted-foreground uppercase tracking-wide">
                          {isApproved ? "Published body" : "Proposed"}
                        </div>
                        <div className="border rounded p-2 max-h-[60vh] overflow-auto whitespace-pre-wrap">
                          {diff.map((r, i) => (
                            <div
                              key={i}
                              className={r.changed ? "bg-red-50 dark:bg-red-950/20" : ""}
                            >
                              {r.base || " "}
                            </div>
                          ))}
                        </div>
                        <div className="border rounded p-2 max-h-[60vh] overflow-auto whitespace-pre-wrap">
                          {diff.map((r, i) => (
                            <div
                              key={i}
                              className={r.changed ? "bg-green-50 dark:bg-green-950/20" : ""}
                            >
                              {r.proposed || " "}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-xs text-muted-foreground">
                        Base document no longer available.
                      </div>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
